import { useTranslation } from "react-i18next";

import type { ActiveUser } from "@/api/active-users";
import type { BomVersion } from "@/api/bom";
import { useActiveUsers } from "@/hooks/useActiveUsers";
import { formatCount } from "@/lib/format";

/** Perfil de uso sem nome: o SKU de usuário sem regra na cascata, ou o usuário não mapeado. */
const NO_PROFILE = "—";

interface ProfileCoverage {
  profile: string;
  licensed: number;
  active: number;
}

interface LicenseCoverage {
  licensed: number;
  active: number;
  profiles: ProfileCoverage[];
}

/**
 * Licenças de usuário da BOM vigente contra os usuários ativos (pessoas).
 *
 * As licenças são as linhas do total com métrica "User", como o
 * ``licensed_users`` dos totais da api; o perfil é o ``usage_profile`` da linha
 * e o ``license_type_name`` do usuário, o mesmo nome da aba Usuários da BOM.
 */
function licenseCoverage(bom: BomVersion, users: ActiveUser[]): LicenseCoverage {
  const byProfile = new Map<string, ProfileCoverage>();
  const entry = (profile: string | null | undefined) => {
    const key = profile ?? NO_PROFILE;
    let item = byProfile.get(key);
    if (!item) {
      item = { profile: key, licensed: 0, active: 0 };
      byProfile.set(key, item);
    }
    return item;
  };
  for (const line of bom.lines) {
    if (!line.in_total || (line.sales_unit_metric ?? "").toLowerCase() !== "user") continue;
    entry(line.usage_profile).licensed += Number(line.qty_current ?? 0);
  }
  for (const user of users) entry(user.license_type_name).active += 1;
  return {
    licensed: bom.totals.licensed_users,
    active: users.length,
    profiles: [...byProfile.values()].sort((a, b) => b.active - a.active || a.profile.localeCompare(b.profile)),
  };
}

/**
 * Aviso do nó 06 quando a BOM licencia menos usuários do que os ativos (#1714).
 *
 * Cartão 6.6 do Marco (22/10/2026): avisa só pelo total, sem bloquear; a
 * diferença por perfil é informação, porque o perfil de cada licença é decisão
 * comercial. Só a BOM gravada é comparada: a calculada já sai dos próprios
 * usuários.
 */
export function LicenseCoverageNote({ projectId, bom }: { projectId: string; bom: BomVersion }) {
  const { t } = useTranslation();
  // A mesma consulta do detalhe do Ato 1: a lista e o cache são um só.
  const active = useActiveUsers(bom.persisted ? projectId : undefined);
  const users = active.data?.users ?? [];
  if (!bom.persisted || users.length === 0) return null;
  const coverage = licenseCoverage(bom, users);
  if (coverage.licensed >= coverage.active) return null;

  return (
    <div
      data-testid="license-coverage"
      className="w-full rounded-md border border-[#f0d58a] bg-[#fffaeb] px-3 py-2 text-[12px] text-[#8a5a00]"
    >
      <p className="font-semibold">
        {t("The BOM licenses fewer users than the active ones. Licenses: {{licensed}} · active users: {{active}}.", {
          licensed: formatCount(coverage.licensed),
          active: formatCount(coverage.active),
        })}
      </p>
      <p className="mt-0.5">
        {t("By profile, for information: the profile of each licence is a commercial decision.")}
      </p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {coverage.profiles.map((item) => (
          <li key={item.profile} data-testid={`license-coverage-${item.profile}`}>
            {t("{{profile}}: licenses {{licensed}} · active users {{active}}", {
              profile: item.profile,
              licensed: formatCount(item.licensed),
              active: formatCount(item.active),
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}
